/* Real HTTP API implementation of Tasks Clients */

import {
  IOperationalDaysClient,
  IOperationalOverviewClient,
  ITasksClient,
  ITaskTemplatesClient,
  ITaskCategoriesClient,
  ITaskStatusesClient,
  ITaskPrioritiesClient,
  ITaskSchedulesClient,
  ITaskReportingDashboardClient,
} from './ITasksClient';
import {
  OperationalDayTemplateDto,
  CreateUpdateOperationalDayTemplateRequest,
  TaskTemplateDto,
  TaskCategoryDto,
  TaskPriorityDto,
  TaskStatusDto,
  TaskScheduleDto,
  TaskDashboardResponse,
  OperationalOverviewResponse,
  TaskSummaryDto,
  TaskDetailDto,
  TaskEvidenceDto,
  TaskEventDto,
  SuccessResponse,
  ClientConfig,
  ApiError,
  BulkReassignRequest,
  BulkPriorityUpdateRequest,
  CreateTaskRequest,
  ReassignTaskRequest,
  BulkReassignTasksRequest,
  ChangePriorityRequest,
  BulkChangePriorityRequest,
} from './tasksTypes';

/* ---- Base Client ---- */
class BaseClient {
  protected baseUrl: string;
  protected fetchFn: typeof fetch;
  protected defaultHeaders: Record<string, string>;

  constructor(cfg: ClientConfig = {}) {
    this.baseUrl = cfg.baseUrl?.replace(/\/$/, '') ?? '';
    this.fetchFn = cfg.fetchFn ?? fetch;
    this.defaultHeaders = cfg.defaultHeaders ?? { 'Content-Type': 'application/json' };
  }

  protected buildUrl(path: string, query?: Record<string, unknown | undefined>): string {
    const url = new URL(path, this.baseUrl || 'http://localhost');
    if (query) {
      Object.entries(query).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        if (Array.isArray(v)) v.forEach(iv => url.searchParams.append(k, this.serializeQueryValue(iv)));
        else url.searchParams.append(k, this.serializeQueryValue(v));
      });
    }
    return url.toString();
  }

  private serializeQueryValue(v: unknown): string {
    return v instanceof Date ? v.toISOString() : String(v);
  }

  protected async request<T>(method: string, url: string, body?: object): Promise<T> {
    const init: RequestInit = { method, headers: this.defaultHeaders };
    if (body !== undefined) init.body = JSON.stringify(body);
    const resp = await this.fetchFn(url, init);
    if (!resp.ok) throw new ApiError(resp.status, await safeRead(resp));
    if (resp.status === 204) return undefined as unknown as T;
    const text = await resp.text();
    return text ? JSON.parse(text) as T : undefined as unknown as T;
  }
}

async function safeRead(resp: Response): Promise<string> {
  try { return await resp.text(); } catch { return ''; }
}

/* ---- API Clients ---- */

export class OperationalDaysClientApi extends BaseClient implements IOperationalDaysClient {
  getOperationalDays(isActive?: boolean, templateType?: string): Promise<SuccessResponse<OperationalDayTemplateDto[]>> {
    return this.request('GET', this.buildUrl('/Tasks/OperationalDays', { isActive, templateType }));
  }
  getOperationalDayById(id: number): Promise<SuccessResponse<OperationalDayTemplateDto>> {
    return this.request('GET', this.buildUrl(`/Tasks/OperationalDays/${id}`));
  }
  createOperationalDay(req: CreateUpdateOperationalDayTemplateRequest): Promise<SuccessResponse<OperationalDayTemplateDto>> {
    return this.request('POST', this.buildUrl('/Tasks/OperationalDays'), req);
  }
  updateOperationalDay(id: number, req: CreateUpdateOperationalDayTemplateRequest): Promise<void> {
    return this.request('PUT', this.buildUrl(`/Tasks/OperationalDays/${id}`), req);
  }
  deleteOperationalDay(id: number): Promise<void> {
    return this.request('DELETE', this.buildUrl(`/Tasks/OperationalDays/${id}`));
  }
}

export class OperationalOverviewClientApi extends BaseClient implements IOperationalOverviewClient {
  getOverview(storeNumber: string, options: {
    day?: string | Date;
    statusIds?: number[];
    categoryIds?: number[];
    priorityIds?: number[];
    userIds?: number[];
    includeScheduled?: boolean;
    includeOperationalDay?: boolean;
    includeAdhoc?: boolean;
  } = {}): Promise<SuccessResponse<OperationalOverviewResponse>> {
    const { day, statusIds, categoryIds, priorityIds, userIds, includeScheduled = true, includeOperationalDay = true, includeAdhoc = true } = options;
    const dayStr = day instanceof Date ? day.toISOString() : day;
    const url = this.buildUrl(`/Tasks/OperationalOverview/${encodeURIComponent(storeNumber)}`,
      { day: dayStr, statusIds, categoryIds, priorityIds, userIds, includeScheduled, includeOperationalDay, includeAdhoc });
    return this.request('GET', url);
  }
  bulkReassign(req: BulkReassignRequest): Promise<void> {
    return this.request('POST', this.buildUrl('/Tasks/OperationalOverview/bulk/reassign'), req);
  }
  bulkPriority(req: BulkPriorityUpdateRequest): Promise<void> {
    return this.request('POST', this.buildUrl('/Tasks/OperationalOverview/bulk/priority'), req);
  }
}

export class TasksClientApi extends BaseClient implements ITasksClient {
  getStoreTasks(storeNumber: string, franchiseName: string, fromDueUtc?: string | Date, toDueUtc?: string | Date): Promise<TaskSummaryDto[]> {
    const q = {
      franchiseName,
      fromDueUtc: fromDueUtc instanceof Date ? fromDueUtc.toISOString() : fromDueUtc,
      toDueUtc: toDueUtc instanceof Date ? toDueUtc.toISOString() : toDueUtc,
    };
    return this.request('GET', this.buildUrl(`/Tasks/Store/${encodeURIComponent(storeNumber)}`, q));
  }
  getTask(taskId: number): Promise<TaskDetailDto> {
    return this.request('GET', this.buildUrl(`/Tasks/${taskId}`));
  }
  getEvidence(taskId: number): Promise<TaskEvidenceDto[]> {
    return this.request('GET', this.buildUrl(`/Tasks/${taskId}/Evidence`));
  }
  getAudit(taskId: number): Promise<TaskEventDto[]> {
    return this.request('GET', this.buildUrl(`/Tasks/${taskId}/Audit`));
  }
  createTask(req: CreateTaskRequest): Promise<number> {
    return this.request('POST', this.buildUrl('/Tasks'), req);
  }
  createTasksBulk(reqs: CreateTaskRequest[]): Promise<number[]> {
    return this.request('POST', this.buildUrl('/Tasks/Bulk'), reqs);
  }
  reassignTask(taskId: number, req: ReassignTaskRequest): Promise<void> {
    return this.request('POST', this.buildUrl(`/Tasks/${taskId}/Reassign`), req);
  }
  bulkReassign(req: BulkReassignTasksRequest): Promise<void> {
    return this.request('POST', this.buildUrl('/Tasks/Reassign/Bulk'), req);
  }
  changePriority(taskId: number, req: ChangePriorityRequest): Promise<void> {
    return this.request('POST', this.buildUrl(`/Tasks/${taskId}/Priority`), req);
  }
  bulkChangePriority(req: BulkChangePriorityRequest): Promise<void> {
    return this.request('POST', this.buildUrl('/Tasks/Priority/Bulk'), req);
  }
  deleteTask(taskId: number): Promise<void> {
    return this.request('DELETE', this.buildUrl(`/Tasks/${taskId}`));
  }
}

export class TaskTemplatesClientApi extends BaseClient implements ITaskTemplatesClient {
  getTemplates(): Promise<TaskTemplateDto[]> {
    return this.request('GET', this.buildUrl('/Tasks/Templates'));
  }
  getTemplate(id: number): Promise<TaskTemplateDto> {
    return this.request('GET', this.buildUrl(`/Tasks/Templates/${id}`));
  }
  createTemplate(dto: TaskTemplateDto): Promise<void> {
    return this.request('POST', this.buildUrl('/Tasks/Templates'), dto);
  }
  updateTemplate(id: number, dto: TaskTemplateDto): Promise<void> {
    return this.request('PUT', this.buildUrl(`/Tasks/Templates/${id}`), dto);
  }
  deleteTemplate(id: number): Promise<void> {
    return this.request('DELETE', this.buildUrl(`/Tasks/Templates/${id}`));
  }
}

export class TaskCategoriesClientApi extends BaseClient implements ITaskCategoriesClient {
  getCategories(): Promise<TaskCategoryDto[]> {
    return this.request('GET', this.buildUrl('/Tasks/Categories'));
  }
  createCategory(dto: TaskCategoryDto, storeNumber?: string): Promise<void> {
    return this.request('POST', this.buildUrl('/Tasks/Categories', { storeNumber }), dto);
  }
  updateCategory(id: number, dto: TaskCategoryDto): Promise<void> {
    return this.request('PUT', this.buildUrl(`/Tasks/Categories/${id}`), dto);
  }
  deleteCategory(id: number): Promise<void> {
    return this.request('DELETE', this.buildUrl(`/Tasks/Categories/${id}`));
  }
}

export class TaskStatusesClientApi extends BaseClient implements ITaskStatusesClient {
  getStatuses(): Promise<TaskStatusDto[]> {
    return this.request('GET', this.buildUrl('/Tasks/Statuses'));
  }
}

export class TaskPrioritiesClientApi extends BaseClient implements ITaskPrioritiesClient {
  getPriorities(): Promise<TaskPriorityDto[]> {
    return this.request('GET', this.buildUrl('/Tasks/Priorities'));
  }
}

export class TaskSchedulesClientApi extends BaseClient implements ITaskSchedulesClient {
  getSchedules(): Promise<TaskScheduleDto[]> {
    return this.request('GET', this.buildUrl('/Tasks/Schedules'));
  }
  getSchedule(id: number): Promise<TaskScheduleDto> {
    return this.request('GET', this.buildUrl(`/Tasks/Schedules/${id}`));
  }
  createSchedule(dto: TaskScheduleDto, storeNumber?: string): Promise<void> {
    return this.request('POST', this.buildUrl('/Tasks/Schedules', { storeNumber }), dto);
  }
  updateSchedule(id: number, dto: TaskScheduleDto): Promise<void> {
    return this.request('PUT', this.buildUrl(`/Tasks/Schedules/${id}`), dto);
  }
  deleteSchedule(id: number): Promise<void> {
    return this.request('DELETE', this.buildUrl(`/Tasks/Schedules/${id}`));
  }
}

export class TaskReportingDashboardClientApi extends BaseClient implements ITaskReportingDashboardClient {
  getDashboard(): Promise<SuccessResponse<TaskDashboardResponse>> {
    return this.request('GET', this.buildUrl('/Tasks/Dashboard'));
  }
}
